import React, { useMemo } from 'react';
import PageSeo from '@/seo/PageSeo';
import { useNavigate } from 'react-router-dom';
import SharedButton from '@/global/Button/Button';
import SharedBadge from '@/global/Badge/Badge';
import { PRODUCT_DEMO_FEATURES } from './productDemo/productDemoFeatures';
import styles from './ProductDemoPage.module.css';
import landingStyles from './ProductDemoLandingTiles.module.css';
import { ChartBarIcon } from '@heroicons/react/24/solid';
import { MagnifyingGlassCircleIcon, LockClosedIcon } from '@heroicons/react/24/outline';

function FeatureIcon({ id }: { id: string }) {
  switch (id) {
    case 'search':
      return (
        <MagnifyingGlassCircleIcon style={{ width: 20, height: 20, color: 'rgb(209, 0, 145)' }} aria-hidden />
      );
    case 'aida':
      return <img src="/static/images/eyesAida.svg" alt="" aria-hidden />;
    case 'advanced-search':
      return (
        <MagnifyingGlassCircleIcon style={{ width: 20, height: 20, color: 'rgb(209, 0, 145)' }} aria-hidden />
      );
    case 'enrichment':
      return (
        <ChartBarIcon
          style={{
            width: 20,
            height: 20,
            color: 'rgb(209, 0, 145)',
            transform: 'translateY(-2px) rotate(-90deg) rotateX(180deg)',
          }}
          aria-hidden
        />
      );
    case 'api':
      return (
        <img src="/static/images/icons/api-icon.svg" alt="" aria-hidden style={{ width: 20, height: 20 }} />
      );
    case 'sticky-notes':
      return (
        <i className="bi bi-sticky-fill" aria-hidden style={{ fontSize: 20, color: 'rgb(209, 0, 145)' }} />
      );
    case 'geo-browse':
      return <ChartBarIcon style={{ width: 20, height: 20, color: 'rgb(209, 0, 145)' }} aria-hidden />;
    default:
      return <LockClosedIcon style={{ width: 20, height: 20, color: 'rgb(209, 0, 145)' }} aria-hidden />;
  }
}

export default function ProductDemoPage() {
  const navigate = useNavigate();
  const tourParams = useMemo(
    () => ({
      demoTour: '1',
    }),
    [],
  );

  const buildTourUrl = (toolFeatureId: string) => {
    const feature = PRODUCT_DEMO_FEATURES.find(f => f.id === toolFeatureId);
    if (!feature) return '/product-demo';

    const isGeoBrowse = feature.id === 'geo-browse';
    if (isGeoBrowse) return feature.toolHref;

    const isSearch = feature.id === 'search';
    const isSpotlight =
      isSearch ||
      feature.id === 'aida' ||
      feature.id === 'advanced-search' ||
      feature.id === 'enrichment' ||
      feature.id === 'api';
    const baseUrl = isSearch
      ? '/examples?view=DISEASES&idents=C0028754&source=ALL&tab=GDA'
      : feature.toolHref;
    const [pathAndHash, queryString = ''] = baseUrl.split('?');
    const [path, hashFragment = ''] = pathAndHash.split('#');
    const hash = hashFragment ? `#${hashFragment}` : '';
    const params = new URLSearchParams(queryString);
    params.set('demoTour', tourParams.demoTour);
    params.set('demoFeature', feature.id);
    if (isSpotlight) params.set('demoStep', '1');
    return `${path}?${params.toString()}${hash}`;
  };

  return (
    <React.Fragment>
      <PageSeo
        title="Interactive Product Demo | DISGENET"
        description="Explore DISGENET search, enrichment, REST API and gene-disease association tools in a guided product demo."
        path="/product-demo"
      />

      <div className={styles.page}>
        <div className={styles.inner}>
          <header className={landingStyles.landingHero}>
            <SharedBadge
              text="TRAINING MODE ACTIVE"
              variant="primaryLight"
              size="xs"
              aria-label="Training mode active badge"
              style={{ marginBottom: 18 }}
            />
            <h1 className={styles.heroTitle}>Explore DISGENET with guided tours</h1>
            <p className={landingStyles.landingHeroLead}>
              Our guided training simulations mirror the LIVE DISGENET environment. Learn to navigate the
              world’s most comprehensive database of human gene–disease associations through guided
              walkthroughs.
            </p>
          </header>

          <section className={landingStyles.modulesSection} aria-label="Training features">
            <div className={landingStyles.modulesHeader}>
              <h2 className={landingStyles.modulesTitle}>Select a Training Module</h2>
              <p className={landingStyles.modulesCount}>{PRODUCT_DEMO_FEATURES.length} Guides Available</p>
            </div>

            <div className={landingStyles.launchGrid}>
              {PRODUCT_DEMO_FEATURES.map(feature => (
                <div key={feature.id} className={landingStyles.launchTile}>
                  <div className={landingStyles.launchIconBox} aria-hidden>
                    <FeatureIcon id={feature.id} />
                  </div>
                  <p className={landingStyles.launchTileTitle}>{feature.label}</p>
                  <p className={landingStyles.launchTileLead}>{feature.tagline}</p>
                  <div className={landingStyles.launchTileFooter}>
                    <SharedButton
                      type="button"
                      variant="secondaryText"
                      size="sm"
                      arrow="right"
                      onClick={() => navigate(buildTourUrl(feature.id))}
                      className={landingStyles.launchGuideBtn}
                    >
                      Launch guide
                    </SharedButton>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </React.Fragment>
  );
}
